import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Share2, MessageCircle, Linkedin, Copy, FileText, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";

const METHODS = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-fiji-green" },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-ocean-deep dark:text-turquoise" },
    { id: "copy", label: "Copy", icon: Copy, color: "text-coral" },
];

const LANGS = [
    { id: "en", label: "English" },
    { id: "fj", label: "Vakaviti" },
    { id: "hi", label: "हिन्दी" },
];

const fmtTs = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-FJ", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
};

export default function AdminTLTBShareAnalyticsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/admin/tltb-share-analytics");
                setData(res.data);
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not load share analytics");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="text-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-coral mx-auto" />
            </div>
        );
    }
    if (!data) return null;

    // Heatmap: max count per cell drives shade
    const maxCount = Math.max(1, ...data.grid.map((g) => g.count));
    const cellShade = (count) => {
        if (count === 0) return "bg-secondary/40 dark:bg-card/60";
        const intensity = count / maxCount;
        if (intensity < 0.2) return "bg-coral/10 dark:bg-coral/20";
        if (intensity < 0.5) return "bg-coral/25 dark:bg-coral/40";
        if (intensity < 0.8) return "bg-coral/45 dark:bg-coral/60";
        return "bg-coral/65 dark:bg-coral/80";
    };

    return (
        <div className="space-y-6" data-testid="admin-tltb-share-analytics">
            {/* HEADER */}
            <Card className="p-5 bg-coral/5 dark:bg-coral/10 border-coral/30">
                <div className="flex items-start gap-3">
                    <Share2 className="h-5 w-5 text-coral mt-0.5 shrink-0" />
                    <div>
                        <div className="font-display text-base font-bold text-foreground mb-1">TLTB Share Analytics</div>
                        <p className="font-app text-sm text-muted-foreground leading-relaxed">
                            Every share dropdown click on <strong className="text-foreground">/tltb-partnership</strong> is logged with method
                            and language. Compare side-by-side with PDF downloads to see which channel × language combinations are converting.
                        </p>
                    </div>
                </div>
            </Card>

            {/* TOTALS STRIP */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-share-totals">
                <Stat label="Total shares" value={data.totals.total} icon={Share2} accent />
                <Stat label="WhatsApp" value={data.totals.by_method.whatsapp || 0} icon={MessageCircle} />
                <Stat label="LinkedIn" value={data.totals.by_method.linkedin || 0} icon={Linkedin} />
                <Stat label="Copy" value={data.totals.by_method.copy || 0} icon={Copy} />
            </div>

            {/* HEATMAP */}
            <Card className="p-5 lg:p-6 bg-card overflow-x-auto" data-testid="admin-share-heatmap">
                <div className="flex items-center gap-2 mb-1.5">
                    <TrendingUp className="h-4 w-4 text-coral" />
                    <h3 className="font-display text-base font-bold text-foreground">Method × Language heatmap</h3>
                </div>
                <p className="font-app text-xs text-muted-foreground mb-4">
                    Darker cells = higher share volume. Pair this with download counts to spot the highest-converting combos.
                </p>

                <div className="min-w-[480px]">
                    {/* Header row */}
                    <div className="grid grid-cols-4 gap-2 mb-2">
                        <div></div>
                        {LANGS.map((l) => (
                            <div key={l.id} className="text-[10px] uppercase tracking-wider font-app font-bold text-muted-foreground text-center">
                                {l.label}
                            </div>
                        ))}
                    </div>
                    {/* Method rows */}
                    {METHODS.map((m) => {
                        const Icon = m.icon;
                        return (
                            <div key={m.id} className="grid grid-cols-4 gap-2 mb-2 items-center">
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${m.color}`} />
                                    <span className="font-app text-xs font-semibold text-foreground">{m.label}</span>
                                </div>
                                {LANGS.map((l) => {
                                    const cell = data.grid.find((g) => g.method === m.id && g.lang === l.id);
                                    const count = cell?.count || 0;
                                    return (
                                        <div
                                            key={l.id}
                                            data-testid={`heatmap-cell-${m.id}-${l.id}`}
                                            className={`rounded-md p-3 text-center border border-border ${cellShade(count)}`}
                                        >
                                            <div className="font-display text-2xl font-bold text-foreground tabular-nums">{count}</div>
                                            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">shares</div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* SHARE → DOWNLOAD COMPARISON */}
            <Card className="p-5 lg:p-6 bg-card" data-testid="admin-share-vs-downloads">
                <div className="flex items-center gap-2 mb-1.5">
                    <FileText className="h-4 w-4 text-coral" />
                    <h3 className="font-display text-base font-bold text-foreground">Share volume vs PDF downloads</h3>
                </div>
                <p className="font-app text-xs text-muted-foreground mb-4">
                    Compare share clicks against actual PDF downloads per language. A high ratio means your localised
                    pack is doing the lead-gen work it was built for.
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                    {LANGS.map((l) => {
                        const shares = data.totals.by_lang[l.id] || 0;
                        const downloads = data.pdf_downloads_by_lang[l.id] || 0;
                        const ratio = shares ? (downloads / shares).toFixed(2) : downloads ? "—" : "0";
                        return (
                            <div key={l.id} className="p-4 rounded-md border border-border bg-secondary/40 dark:bg-card/60">
                                <div className="font-display text-sm font-bold text-foreground mb-2">{l.label}</div>
                                <div className="space-y-1.5 font-app text-xs">
                                    <Row label="Shares" value={shares} />
                                    <Row label="PDF downloads" value={downloads} accent />
                                    <Row label="Download / share ratio" value={ratio} muted />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* RECENT TIMELINE */}
            <Card className="overflow-hidden bg-card" data-testid="admin-share-recent">
                <div className="p-4 border-b border-border">
                    <h3 className="font-display text-base font-bold text-foreground">Recent shares</h3>
                    <p className="font-app text-xs text-muted-foreground">Last 50 events.</p>
                </div>
                {(data.recent || []).length === 0 ? (
                    <div className="p-8 text-center font-app text-sm text-muted-foreground">
                        No shares yet — the first one shows up the moment a TLTB exec hits the share button.
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {data.recent.map((s) => {
                            const m = METHODS.find((x) => x.id === s.method);
                            const Icon = m?.icon || Share2;
                            return (
                                <div key={s.id} className="px-4 py-3 flex items-center gap-3 text-sm font-app">
                                    <Icon className={`h-4 w-4 shrink-0 ${m?.color || "text-coral"}`} />
                                    <Badge variant="outline" className="text-[10px] capitalize border-muted-foreground/30 shrink-0">
                                        {s.method}
                                    </Badge>
                                    <Badge variant="outline" className="text-[10px] uppercase border-muted-foreground/30 shrink-0">
                                        {s.lang}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground truncate flex-1 font-mono">{s.ip || "—"}</span>
                                    <span className="text-xs text-muted-foreground shrink-0">{fmtTs(s.created_at)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
}

function Stat({ label, value, icon: Icon, accent }) {
    return (
        <Card className={`p-4 ${accent ? "bg-coral/5 dark:bg-coral/10 border-coral/20" : "bg-card"}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`h-3.5 w-3.5 ${accent ? "text-coral" : "text-muted-foreground"}`} />
                <span className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums ${accent ? "text-coral" : "text-foreground"}`}>
                {value}
            </div>
        </Card>
    );
}

function Row({ label, value, muted, accent }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] text-muted-foreground">{label}</span>
            <span className={`tabular-nums ${accent ? "text-fiji-green font-bold" : muted ? "text-muted-foreground" : "text-foreground font-semibold"}`}>
                {value}
            </span>
        </div>
    );
}
